import React, { useEffect, useState } from "react";
import {
    Button,
    Typography,
    CircularProgress,
    Box,
    useMediaQuery,
} from "@mui/material";
import { Check, HowToVote, MilitaryTech } from "@mui/icons-material";
import { motion } from "framer-motion";
import BackdropOverlay, {
    BACKDROP_OVERLAY_Z_INDEX,
    DIALOG_OVERLAY_TOP_OFFSET,
} from "../common/BackdropOverlay.jsx";
import { getMapMetaById, getMapImageUrl } from "../../constants/mapMeta";
import {
    MAP_VOTE_PENDING_HEADING,
    MAP_VOTE_PENDING_HINT,
} from "../../constants/mapVote";
import utils from "../../universal/utils";
import { colors } from "../../theme/colors";
import { DIALOG_ACCEPT_BUTTON_SX } from "../common/dialogActionButtonSx";
import {
    MOBILE_NAV_HEIGHT,
    MOBILE_NAV_MEDIA,
} from "../layout/layoutConstants";

const MotionBox = motion(Box);

const DOTS_SX = {
    display: "inline-block",
    ml: "3px",
    "@keyframes blink": {
        "0%": { opacity: 0 },
        "20%": { opacity: 1 },
        "100%": { opacity: 0 },
    },
    "& span": {
        animation: "blink 1.5s infinite",
        opacity: 0,
        animationFillMode: "forwards",
        fontSize: "0.95rem",
        fontWeight: "bold",
        ml: "1px",
    },
    "& span:nth-of-type(1)": { animationDelay: "0s" },
    "& span:nth-of-type(2)": { animationDelay: "0.3s" },
    "& span:nth-of-type(3)": { animationDelay: "0.6s" },
};

/** Same AAA gold treatment as Join Party. */
const acceptMatchButtonStyles = {
    ...DIALOG_ACCEPT_BUTTON_SX,
    minHeight: { xs: "3.05rem", md: "2.85rem" },
    width: { xs: "100%", md: "57.5%" },
    maxWidth: { xs: "100%", md: "22rem" },
    fontSize: { xs: "0.95rem", md: "1rem" },
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    zIndex: 11,
};

function StatusDots() {
    return (
        <Box component="span" sx={DOTS_SX}>
            <span>.</span>
            <span>.</span>
            <span>.</span>
        </Box>
    );
}

function getFooterStatusText({ allAccepted, hasAccepted }) {
    if (allAccepted) {
        return "All players ready! Deploying";
    }
    if (hasAccepted) {
        return "You accepted — waiting for match confirmations";
    }
    return "Waiting for match confirmations";
}

export default function AcceptMatchDialog({
    matchId,
    onAcceptMatch,
    timeoutSeconds = 30,
    matchAcceptDialogState,
}) {
    const [open, setOpen] = useState(true);
    const [minimized, setMinimized] = useState(false);
    const [timeLeft, setTimeLeft] = useState(timeoutSeconds);
    const [accepted, setAccepted] = useState(false);
    const isMobile = useMediaQuery(MOBILE_NAV_MEDIA);
    const mapId = matchAcceptDialogState?.map || null;
    const mapVotePending = Boolean(matchAcceptDialogState?.mapVotePending);
    const mapMeta = getMapMetaById(mapId);
    const mapImageUrl = getMapImageUrl(mapId, utils.getEdgeBasePath());

    useEffect(() => {
        setOpen(true);
        setMinimized(false);
        setTimeLeft(timeoutSeconds);
        setAccepted(Boolean(matchAcceptDialogState?.accepted));
    }, [matchId, timeoutSeconds]);

    useEffect(() => {
        if (matchAcceptDialogState?.accepted) {
            setAccepted(true);
        }
    }, [matchAcceptDialogState?.accepted]);

    const totalPlayers = matchAcceptDialogState?.totalPlayers || 2;
    const acceptedCount = matchAcceptDialogState?.acceptedCount || 0;
    const allAccepted = Boolean(matchAcceptDialogState?.allAccepted);
    const hasAccepted = accepted || Boolean(matchAcceptDialogState?.accepted);

    useEffect(() => {
        if (allAccepted && minimized) {
            setMinimized(false);
            setOpen(true);
        }
    }, [allAccepted, minimized]);

    useEffect(() => {
        if (timeLeft <= 0) return;

        const timer = setTimeout(() => {
            setTimeLeft((prevTime) => prevTime - 1);
        }, 1000);

        return () => clearTimeout(timer);
    }, [timeLeft]);

    useEffect(() => {
        if (timeLeft <= 0 && (open || minimized) && !allAccepted) {
            setOpen(false);
            setMinimized(false);
        }
    }, [timeLeft, open, minimized, allAccepted]);

    const handleClose = () => {
        setOpen(false);
        setMinimized(true);
    };

    const handleReopen = () => {
        setMinimized(false);
        setOpen(true);
    };

    const handleAccept = () => {
        setAccepted(true);
        onAcceptMatch(matchId);
    };

    const progressSize = isMobile ? 92 : 76;
    const progressThickness = isMobile ? 4.5 : 3;
    const squadSlotColumns = Math.min(Math.max(totalPlayers, 1), 5);
    const progressValue =
        totalPlayers > 0 ? (acceptedCount / totalPlayers) * 100 : 0;
    const urgencyThreshold = Math.ceil(timeoutSeconds / 3);
    const isUrgent = timeLeft <= urgencyThreshold && timeLeft > 0;
    const footerStatusText = getFooterStatusText({ allAccepted, hasAccepted });

    return (
        <>
            <BackdropOverlay
                open={open}
                onClick={handleClose}
                mountWhenClosed
                zIndex={BACKDROP_OVERLAY_Z_INDEX}
                sx={{
                    px: { xs: 1.25, sm: 2 },
                    alignItems: "flex-start",
                    pt: DIALOG_OVERLAY_TOP_OFFSET,
                    pb: {
                        xs: `calc(${MOBILE_NAV_HEIGHT} + 0.65rem + env(safe-area-inset-bottom, 0px))`,
                        md: 0,
                    },
                    overflowY: "auto",
                }}
                contentSx={{
                    width: "100%",
                    maxWidth: { xs: "100%", md: "36rem" },
                }}
            >
                <MotionBox
                    initial={{
                        opacity: 0,
                        scale: 0.94,
                        y: 18,
                        filter: "blur(10px)",
                    }}
                    animate={{
                        opacity: 1,
                        scale: 1,
                        y: 0,
                        filter: "blur(0px)",
                    }}
                    transition={{
                        duration: 0.42,
                        ease: [0.22, 1, 0.36, 1],
                    }}
                    sx={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "36rem",
                        borderRadius: { xs: "18px", md: "26px" },
                        overflow: "hidden",
                        background: `
                            linear-gradient(
                                180deg,
                                rgba(18,18,22,0.985) 0%,
                                rgba(11,11,15,0.992) 58%,
                                rgba(7,7,10,1) 100%
                            )
                        `,
                        border: `1px solid ${colors.border.default}`,
                        backdropFilter: "blur(22px)",
                        boxShadow: `
                            0 28px 80px rgba(0,0,0,0.72),
                            0 0 40px rgba(232,163,61,0.08),
                            inset 0 1px 0 rgba(255,255,255,0.05)
                        `,
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            pointerEvents: "none",
                            background: `
                                radial-gradient(
                                    circle at top,
                                    rgba(232,163,61,0.13),
                                    transparent 56%
                                )
                            `,
                        },

                        "&::after": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            pointerEvents: "none",
                            opacity: 0.035,
                            backgroundImage:
                                "repeating-linear-gradient(0deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, transparent 2px, transparent 4px)",
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            height: "3px",
                            backgroundColor: "rgba(255,255,255,0.04)",
                            overflow: "hidden",
                        }}
                    >
                        <MotionBox
                            animate={{
                                width: `${Math.max(
                                    0,
                                    (timeLeft / timeoutSeconds) * 100
                                )}%`,
                            }}
                            transition={{
                                duration: 1,
                                ease: "linear",
                            }}
                            sx={{
                                height: "100%",
                                background: isUrgent
                                    ? `
                                    linear-gradient(
                                        90deg,
                                        ${colors.state.danger.main},
                                        #ff8a8a,
                                        ${colors.state.danger.main}
                                    )
                                `
                                    : `
                                    linear-gradient(
                                        90deg,
                                        ${colors.primary.main},
                                        ${colors.accent.main},
                                        ${colors.primary.main}
                                    )
                                `,
                                backgroundSize: "200% 100%",
                                animation: "energyFlow 2s linear infinite",
                                boxShadow: isUrgent
                                    ? "0 0 14px rgba(220,38,38,0.55)"
                                    : colors.glow.soft,
                                "@keyframes energyFlow": {
                                    "0%": {
                                        backgroundPosition: "0% 50%",
                                    },
                                    "100%": {
                                        backgroundPosition: "200% 50%",
                                    },
                                },
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 1,
                            p: {
                                xs: "1.15rem",
                                sm: "1.25rem",
                                md: "1.35rem",
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: { xs: "0.7rem", md: "0.9rem" },
                                mb: { xs: "1rem", md: "0.9rem" },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: { xs: "0.7rem", md: "0.8rem" },
                                    minWidth: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: { xs: "2.85rem", md: "3.3rem" },
                                        height: { xs: "2.85rem", md: "3.3rem" },
                                        borderRadius: "50%",
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: `
                                            radial-gradient(
                                                circle at top,
                                                rgba(232,163,61,0.28),
                                                rgba(232,163,61,0.05)
                                            )
                                        `,
                                        border: `1px solid rgba(232,163,61,0.18)`,
                                        boxShadow: `
                                            0 0 22px rgba(232,163,61,0.14),
                                            inset 0 1px 0 rgba(255,255,255,0.06)
                                        `,
                                    }}
                                >
                                    <MilitaryTech
                                        sx={{
                                            fontSize: {
                                                xs: "1.35rem",
                                                md: "1.55rem",
                                            },
                                            color: colors.accent.main,
                                            filter:
                                                "drop-shadow(0 0 10px rgba(232,163,61,0.32))",
                                        }}
                                    />
                                </Box>

                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: {
                                                xs: "1.2rem",
                                                md: "1.35rem",
                                            },
                                            fontWeight: 600,
                                            color: colors.text.primary,
                                            lineHeight: 1,
                                        }}
                                    >
                                        Match Found
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: "0.28rem",
                                            color: colors.text.secondary,
                                            fontSize: {
                                                xs: "0.72rem",
                                                md: "0.76rem",
                                            },
                                            letterSpacing: "0.04em",
                                        }}
                                    >
                                        {mapVotePending
                                            ? MAP_VOTE_PENDING_HINT
                                            : "Competitive lobby ready"}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    minWidth: { xs: "3.1rem", md: "3.35rem" },
                                    height: { xs: "3.1rem", md: "3.35rem" },
                                    borderRadius: "14px",
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: isUrgent
                                        ? "rgba(220,38,38,0.10)"
                                        : "rgba(232,163,61,0.08)",
                                    border: isUrgent
                                        ? `1px solid rgba(220,38,38,0.24)`
                                        : `1px solid rgba(232,163,61,0.16)`,
                                    boxShadow: isUrgent
                                        ? "0 0 16px rgba(220,38,38,0.14)"
                                        : "0 0 18px rgba(232,163,61,0.08)",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: {
                                            xs: "0.95rem",
                                            md: "0.98rem",
                                        },
                                        fontWeight: 600,
                                        color: isUrgent
                                            ? colors.state.danger.main
                                            : colors.primary.main,
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    {timeLeft}s
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    md: "1.15fr 0.85fr",
                                },
                                gap: { xs: "0.85rem", md: "0.9rem" },
                                alignItems: "stretch",
                                mb: { xs: "1rem", md: "0.95rem" },
                            }}
                        >
                            {(mapVotePending || mapId) && (
                                <Box
                                    sx={{
                                        position: "relative",
                                        overflow: "hidden",
                                        minHeight: {
                                            xs: "12.5rem",
                                            sm: "13.5rem",
                                            md: "10rem",
                                        },
                                        borderRadius: {
                                            xs: "14px",
                                            md: "18px",
                                        },
                                        border: `1px solid ${colors.border.default}`,
                                        backgroundColor:
                                            colors.background.surface,
                                        boxShadow: `
                                    0 16px 34px rgba(0,0,0,0.38),
                                    inset 0 1px 0 rgba(255,255,255,0.04)
                                `,
                                    }}
                                >
                                    {mapVotePending ? (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                justifyContent: "flex-end",
                                                p: "0.85rem",
                                                background: `
                                                    linear-gradient(
                                                        180deg,
                                                        rgba(232,163,61,0.10) 0%,
                                                        rgba(0,0,0,0.18) 42%,
                                                        rgba(0,0,0,0.72) 100%
                                                    )
                                                `,
                                            }}
                                        >
                                            <HowToVote
                                                sx={{
                                                    fontSize: "1.45rem",
                                                    color: colors.primary.main,
                                                    mb: "0.45rem",
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    color: colors.text.secondary,
                                                    fontSize: "0.6rem",
                                                    fontWeight: 600,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.14em",
                                                    mb: "0.2rem",
                                                }}
                                            >
                                                Up next
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: colors.text.primary,
                                                    fontSize: {
                                                        xs: "1.35rem",
                                                        md: "1.25rem",
                                                    },
                                                    fontWeight: 600,
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {MAP_VOTE_PENDING_HEADING}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <>
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            backgroundImage: mapImageUrl
                                                ? `url(${mapImageUrl})`
                                                : "none",
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            background: `
                                        linear-gradient(
                                            180deg,
                                            rgba(0,0,0,0.06) 0%,
                                            rgba(0,0,0,0.46) 58%,
                                            rgba(0,0,0,0.92) 100%
                                        )
                                    `,
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            background: `
                                        radial-gradient(
                                            circle at top left,
                                            rgba(232,163,61,0.14),
                                            transparent 42%
                                        )
                                    `,
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            position: "absolute",
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            p: "0.85rem",
                                            textAlign: "left",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color: colors.text.secondary,
                                                fontSize: "0.6rem",
                                                fontWeight: 600,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.14em",
                                                mb: "0.2rem",
                                            }}
                                        >
                                            Deploying To
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: colors.text.primary,
                                                fontSize: {
                                                    xs: "1.35rem",
                                                    md: "1.25rem",
                                                },
                                                fontWeight: 600,
                                                lineHeight: 1,
                                                textShadow:
                                                    "0 6px 20px rgba(0,0,0,0.65)",
                                            }}
                                        >
                                            {mapMeta?.display || mapId}
                                        </Typography>
                                    </Box>
                                        </>
                                    )}
                                </Box>
                            )}

                            <Box
                                sx={{
                                    borderRadius: {
                                        xs: "14px",
                                        md: "18px",
                                    },
                                    border: `1px solid ${colors.border.default}`,
                                    background: `
                                    linear-gradient(
                                        180deg,
                                        rgba(255,255,255,0.025),
                                        rgba(255,255,255,0.01)
                                    )
                                `,
                                    p: { xs: "1rem", md: "0.85rem" },
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    gap: { xs: "1rem", md: 0 },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: {
                                            xs: "flex-start",
                                            md: "center",
                                        },
                                        gap: { xs: "1rem", md: "0.9rem" },
                                        mb: { xs: 0, md: "0.85rem" },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: "relative",
                                            width: progressSize,
                                            height: progressSize,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <CircularProgress
                                            variant="determinate"
                                            value={100}
                                            size={progressSize}
                                            thickness={progressThickness}
                                            sx={{
                                                position: "absolute",
                                                color: "rgba(255,255,255,0.05)",
                                            }}
                                        />

                                        <CircularProgress
                                            variant="determinate"
                                            value={progressValue}
                                            size={progressSize}
                                            thickness={progressThickness}
                                            sx={{
                                                color: colors.primary.main,
                                                filter:
                                                    "drop-shadow(0 0 10px rgba(232,163,61,0.22))",
                                                "& .MuiCircularProgress-circle":
                                                    {
                                                        strokeLinecap: "round",
                                                    },
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: {
                                                        xs: "1.55rem",
                                                        md: "1.35rem",
                                                    },
                                                    fontWeight: 900,
                                                    color: colors.primary.main,
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {acceptedCount}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: {
                                                        xs: "0.68rem",
                                                        md: "0.6rem",
                                                    },
                                                    fontWeight: 700,
                                                    color: colors.text.muted,
                                                }}
                                            >
                                                / {totalPlayers}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            sx={{
                                                color: colors.text.secondary,
                                                fontSize: {
                                                    xs: "0.68rem",
                                                    md: "0.62rem",
                                                },
                                                fontWeight: 800,
                                                letterSpacing: "0.12em",
                                                textTransform: "uppercase",
                                                mb: "0.3rem",
                                            }}
                                        >
                                            Squad Status
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: colors.text.primary,
                                                fontSize: {
                                                    xs: "1rem",
                                                    md: "0.88rem",
                                                },
                                                fontWeight: 700,
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {acceptedCount}/{totalPlayers}{" "}
                                            players ready
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: "0.28rem",
                                                color: colors.text.muted,
                                                fontSize: {
                                                    xs: "0.74rem",
                                                    md: "0.68rem",
                                                },
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            Waiting for remaining confirmations
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: `repeat(${squadSlotColumns}, minmax(0, 1fr))`,
                                        gap: {
                                            xs: "0.5rem",
                                            md: "0.38rem",
                                        },
                                    }}
                                >
                                    {Array.from({ length: totalPlayers }).map(
                                        (_, index) => {
                                            const isPlayerAccepted =
                                                index < acceptedCount;

                                            return (
                                                <MotionBox
                                                    key={index}
                                                    animate={{
                                                        scale: isPlayerAccepted
                                                            ? [1, 1.06, 1]
                                                            : 1,
                                                    }}
                                                    transition={{
                                                        duration: 0.32,
                                                    }}
                                                    sx={{
                                                        height: {
                                                            xs: "2.65rem",
                                                            md: "2.1rem",
                                                        },
                                                        borderRadius: "10px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "center",
                                                        transition:
                                                            "all 220ms cubic-bezier(0.22,1,0.36,1)",
                                                        ...(isPlayerAccepted
                                                            ? {
                                                                  background:
                                                                      "rgba(232,163,61,0.12)",
                                                                  border: `
                                                            1px solid rgba(232,163,61,0.32)
                                                        `,
                                                                  boxShadow:
                                                                      "0 0 10px rgba(232,163,61,0.10)",
                                                              }
                                                            : {
                                                                  background:
                                                                      "rgba(255,255,255,0.02)",
                                                                  border: `
                                                            1px solid rgba(255,255,255,0.05)
                                                        `,
                                                              }),
                                                    }}
                                                >
                                                    {isPlayerAccepted ? (
                                                        <Check
                                                            sx={{
                                                                fontSize: {
                                                                    xs: "1rem",
                                                                    md: "0.85rem",
                                                                },
                                                                color: colors
                                                                    .primary
                                                                    .main,
                                                            }}
                                                        />
                                                    ) : (
                                                        <Typography
                                                            sx={{
                                                                fontSize: {
                                                                    xs: "0.78rem",
                                                                    md: "0.64rem",
                                                                },
                                                                fontWeight: 800,
                                                                color: colors
                                                                    .text.muted,
                                                            }}
                                                        >
                                                            {index + 1}
                                                        </Typography>
                                                    )}
                                                </MotionBox>
                                            );
                                        }
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: { xs: "1rem", md: "0.95rem" },
                                minHeight: "1.2rem",
                                px: { xs: "0.25rem", md: 0 },
                                textAlign: "center",
                            }}
                        >
                            <Typography
                                sx={{
                                    color: allAccepted
                                        ? colors.primary.main
                                        : colors.text.secondary,
                                    fontSize: {
                                        xs: "0.84rem",
                                        md: "0.9rem",
                                    },
                                    fontWeight: allAccepted ? 700 : 600,
                                    letterSpacing: "0.02em",
                                }}
                            >
                                {footerStatusText}
                                <StatusDots />
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <Button
                                onClick={handleAccept}
                                disabled={hasAccepted}
                                sx={acceptMatchButtonStyles}
                            >
                                {hasAccepted ? "Ready" : "Accept Match"}
                            </Button>
                        </Box>
                    </Box>
                </MotionBox>
            </BackdropOverlay>

            {minimized && timeLeft > 0 && (
                <MotionBox
                    initial={{ opacity: 0, y: 24, scale: 0.94 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
                    sx={{
                        position: "fixed",
                        bottom: {
                            xs: `calc(${MOBILE_NAV_HEIGHT} + 0.55rem + env(safe-area-inset-bottom, 0px))`,
                            md: "1.75rem",
                        },
                        right: { xs: "0.7rem", md: "1.75rem" },
                        left: { xs: "0.7rem", md: "auto" },
                        zIndex: 1400,
                    }}
                >
                    <Box
                        onClick={handleReopen}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                handleReopen();
                            }
                        }}
                        sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: "0.55rem", md: "0.9rem" },
                            width: { xs: "100%", md: "auto" },
                            minWidth: { xs: 0, md: "22rem" },
                            px: { xs: "0.7rem", md: "1.1rem" },
                            py: { xs: "0.55rem", md: "0.95rem" },
                            borderRadius: { xs: "14px", md: "20px" },
                            background: `
                                linear-gradient(
                                    180deg,
                                    rgba(28,30,34,0.92) 0%,
                                    rgba(16,18,22,0.96) 100%
                                )
                            `,
                            border: isUrgent
                                ? "1px solid rgba(255,88,88,0.35)"
                                : `1px solid rgba(232,163,61,0.22)`,
                            backdropFilter: "blur(18px)",
                            boxShadow: isUrgent
                                ? `
                                    0 18px 48px rgba(0,0,0,0.55),
                                    0 0 32px rgba(255,88,88,0.18)
                                `
                                : `
                                    0 18px 48px rgba(0,0,0,0.55),
                                    0 0 32px rgba(232,163,61,0.12)
                                `,
                            animation: isUrgent
                                ? "widgetUrgentPulse 1.4s ease-in-out infinite"
                                : "widgetGlow 2.4s ease-in-out infinite",
                            transition:
                                "transform 220ms cubic-bezier(0.22,1,0.36,1)",
                            "@keyframes widgetGlow": {
                                "0%, 100%": {
                                    boxShadow: `
                                        0 18px 48px rgba(0,0,0,0.55),
                                        0 0 24px rgba(232,163,61,0.10)
                                    `,
                                },
                                "50%": {
                                    boxShadow: `
                                        0 18px 48px rgba(0,0,0,0.55),
                                        0 0 36px rgba(232,163,61,0.22)
                                    `,
                                },
                            },
                            "@keyframes widgetUrgentPulse": {
                                "0%, 100%": {
                                    boxShadow: `
                                        0 18px 48px rgba(0,0,0,0.55),
                                        0 0 24px rgba(255,88,88,0.18)
                                    `,
                                },
                                "50%": {
                                    boxShadow: `
                                        0 18px 48px rgba(0,0,0,0.55),
                                        0 0 40px rgba(255,88,88,0.32)
                                    `,
                                },
                            },
                            "&:hover": {
                                transform: "translateY(-3px)",
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: { xs: "2.1rem", md: "2.75rem" },
                                height: { xs: "2.1rem", md: "2.75rem" },
                                borderRadius: { xs: "10px", md: "14px" },
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                background: "rgba(232,163,61,0.12)",
                                border: "1px solid rgba(232,163,61,0.28)",
                                boxShadow: "0 0 18px rgba(232,163,61,0.14)",
                            }}
                        >
                            <MilitaryTech
                                sx={{
                                    fontSize: { xs: "1.15rem", md: "1.45rem" },
                                    color: colors.accent.main,
                                }}
                            />
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                            <Typography
                                sx={{
                                    color: colors.text.primary,
                                    fontSize: { xs: "0.8rem", md: "0.92rem" },
                                    fontWeight: 800,
                                    letterSpacing: "0.04em",
                                    lineHeight: 1.15,
                                }}
                            >
                                {hasAccepted ? "Match Accepted" : "Match Ready"}
                            </Typography>
                            <Typography
                                sx={{
                                    mt: { xs: "0.1rem", md: "0.2rem" },
                                    color: colors.text.secondary,
                                    fontSize: { xs: "0.68rem", md: "0.78rem" },
                                    fontWeight: 500,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {mapMeta?.display || mapId || "Competitive queue"}{" "}
                                ·{" "}
                                {hasAccepted
                                    ? "Waiting for squad"
                                    : "Tap to accept"}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                flexShrink: 0,
                                px: { xs: "0.55rem", md: "0.75rem" },
                                py: { xs: "0.28rem", md: "0.45rem" },
                                borderRadius: { xs: "9px", md: "12px" },
                                background: isUrgent
                                    ? "rgba(255,88,88,0.16)"
                                    : "rgba(232,163,61,0.12)",
                                border: isUrgent
                                    ? "1px solid rgba(255,88,88,0.28)"
                                    : "1px solid rgba(232,163,61,0.22)",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: { xs: "0.82rem", md: "0.95rem" },
                                    fontWeight: 800,
                                    color: isUrgent
                                        ? colors.state.danger.main
                                        : colors.primary.main,
                                    fontVariantNumeric: "tabular-nums",
                                    letterSpacing: "0.06em",
                                }}
                            >
                                {timeLeft}s
                            </Typography>
                        </Box>
                    </Box>
                </MotionBox>
            )}
        </>
    );
}
